import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useApi } from "@/core/api";
import { usePackChatRequest } from "@/payroll/pack-chat/conditional-request";
import {
  PACK_CHAT_STALE_TIME,
  packChatQueryOptions,
} from "@/payroll/pack-chat/refresh";
import { selectPackChatTechnicianGroups } from "@/payroll/pack-chat/technician-groups";
import type {
  IPackChatStartOptionsResponse,
  IPackChatThreadDetailResponse,
  IPackChatThreadOverview,
  IPackChatThreadsResponse,
  PackChatStatus,
} from "@/payroll/pack-chat/types";
import { currentPayrollResult } from "@/payroll/teams/query-result";
import { useCurrentPackChatResult } from "./current-result";
import {
  managedPackStartOptionsKey,
  managedPackThreadKey,
  managedPackThreadsKey,
  path,
} from "./keys";

export const useGetManagedPackThreads = <TData = IPackChatThreadsResponse>(
  businessId: string | null,
  periodStart: string | null,
  options?: {
    enabled?: boolean;
    select?: (data: IPackChatThreadsResponse) => TData;
  },
) => {
  const request = usePackChatRequest(businessId);

  const query = useQuery({
    queryKey: managedPackThreadsKey(businessId, periodStart),
    queryFn: ({ signal }) => {
      if (!businessId || !periodStart)
        throw new Error("Pay period is required");

      return request<IPackChatThreadsResponse>(
        path(businessId, periodStart),
        managedPackThreadsKey(businessId, periodStart),
        signal,
      );
    },
    select: options?.select,
    enabled: (options?.enabled ?? true) && !!businessId && !!periodStart,
    ...packChatQueryOptions,
    refetchOnMount: "always",
  });
  return useCurrentPackChatResult(
    query,
    JSON.stringify(managedPackThreadsKey(businessId, periodStart)),
  );
};

export const useGetManagedPackThreadGroups = (
  businessId: string | null,
  periodStart: string | null,
  filters?: { status?: PackChatStatus; search?: string },
) =>
  useGetManagedPackThreads<IPackChatThreadOverview>(businessId, periodStart, {
    select: useMemo(
      () =>
        selectPackChatTechnicianGroups({
          status: filters?.status,
          search: filters?.search,
        }),
      [filters?.status, filters?.search],
    ),
  });

export const useGetManagedPackThread = (
  businessId: string | null,
  periodStart: string | null,
  threadId: string | null,
) => {
  const request = usePackChatRequest(businessId);

  const query = useQuery({
    queryKey: managedPackThreadKey(businessId, periodStart, threadId),
    queryFn: ({ signal }) => {
      if (!businessId || !periodStart || !threadId) {
        throw new Error("Thread is required");
      }
      return request<IPackChatThreadDetailResponse>(
        `${path(businessId, periodStart)}/${threadId}`,
        managedPackThreadKey(businessId, periodStart, threadId),
        signal,
      );
    },
    enabled: !!businessId && !!periodStart && !!threadId,
    ...packChatQueryOptions,
    refetchOnMount: "always",
  });
  return useCurrentPackChatResult(
    query,
    JSON.stringify(managedPackThreadKey(businessId, periodStart, threadId)),
  );
};

export const useGetManagedPackStartOptions = (
  businessId: string | null,
  periodStart: string | null,
  enabled = true,
) => {
  const api = useApi();

  const query = useQuery({
    queryKey: managedPackStartOptionsKey(businessId, periodStart),
    queryFn: () => {
      if (!businessId || !periodStart)
        throw new Error("Pay period is required");
      return api
        .get(`${path(businessId, periodStart)}/start_options`)
        .then((res) => res.data as IPackChatStartOptionsResponse);
    },
    enabled: enabled && !!businessId && !!periodStart,
    retry: false,
    staleTime: PACK_CHAT_STALE_TIME,
    refetchOnMount: "always",
  });
  return currentPayrollResult(query);
};
